import { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { faPen, faUserShield, faBan, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { Table, Badge, Avatar, Button, KebabMenu, Icon } from '../ui';
import type { TableColumn, BadgeTone } from '../ui';
import useMediaQuery, { PHONE_QUERY } from '../clients/useMediaQuery';

// Phones keep User + actions; role, agency and status fold into the user cell
const PHONE_HIDDEN = new Set(['agency_id', 'role', 'is_banned', 'last_sign_in_at', 'created_at']);

interface User {
  id: string;
  email: string;
  role: string;
  name: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_banned: boolean;
  agency_id: string;
  agencies?: {
    name: string;
  };
}

interface UserTableProps {
  users: User[];
  currentUserId: string;
  isLoading: boolean;
  searchTerm: string;
  onEditUser: (user: User) => void;
  onBanStatusChange: (userId: string, isBanned: boolean) => Promise<void>;
  onRoleChange: (userId: string) => Promise<void>;
  onError: (error: string) => void;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  developer: 'Developer',
  client: 'Client',
  agency_owner: 'Agency Owner',
  authenticated: 'User',
};

const ROLE_TONES: Record<string, BadgeTone> = {
  admin: 'accent',
  developer: 'info',
};

const roleLabel = (role: string) =>
  ROLE_LABELS[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : role);

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function UserTable({
  users,
  currentUserId,
  isLoading,
  searchTerm,
  onEditUser,
  onBanStatusChange,
  onRoleChange,
  onError,
}: UserTableProps) {
  const phone = useMediaQuery(PHONE_QUERY);
  const [confirmAction, setConfirmAction] = useState<{
    open: boolean;
    userId: string;
    type: 'ban' | 'unban' | 'makeAdmin';
    email: string;
    isProcessing: boolean;
  }>({
    open: false,
    userId: '',
    type: 'ban',
    email: '',
    isProcessing: false,
  });

  const handleConfirmAction = useCallback(async () => {
    try {
      setConfirmAction((prev) => ({ ...prev, isProcessing: true }));

      if (confirmAction.type === 'ban') {
        await onBanStatusChange(confirmAction.userId, false);
      } else if (confirmAction.type === 'unban') {
        await onBanStatusChange(confirmAction.userId, true);
      } else if (confirmAction.type === 'makeAdmin') {
        await onRoleChange(confirmAction.userId);
      }

      // Close the dialog
      setConfirmAction((prev) => ({
        ...prev,
        open: false,
        isProcessing: false,
      }));
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
      setConfirmAction((prev) => ({ ...prev, isProcessing: false }));
    }
  }, [confirmAction.type, confirmAction.userId, onBanStatusChange, onRoleChange, onError]);

  // Add keyboard event listener for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && confirmAction.open && !confirmAction.isProcessing) {
        event.preventDefault();
        handleConfirmAction();
      }
    };

    if (confirmAction.open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [confirmAction.open, confirmAction.isProcessing, handleConfirmAction]);

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
        <div
          className="animate-spin"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-pill)',
            border: '3px solid var(--border-hairline)',
            borderTopColor: 'var(--accent)',
            marginBottom: 'var(--space-3)',
          }}
        />
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading...</p>
      </div>
    );
  }

  if (filteredUsers.length === 0) {
    return (
      <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
        <Icon name="inbox" size={24} style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }} />
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          {searchTerm ? 'No users match that search.' : 'No users yet. Start by inviting some.'}
        </p>
      </div>
    );
  }

  const columns: TableColumn<User>[] = [
    {
      key: 'email',
      label: 'User',
      wrap: phone,
      render: (_value, user) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', opacity: user.is_banned ? 0.55 : 1 }}>
          <Avatar
            name={user.name || user.email}
            size={30}
            presence={user.is_banned ? undefined : 'online'}
          />
          <span style={{ minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
              }}
            >
              {user.name || user.email.split('@')[0]}
            </span>
            <span
              className="t-num"
              style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}
            >
              {user.email}
            </span>
            {phone ? (
              <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginTop: '5px' }}>
                <Badge tone={ROLE_TONES[user.role] || 'neutral'}>{roleLabel(user.role)}</Badge>
                {user.is_banned ? <Badge tone="idle">Inactive</Badge> : null}
                {user.agencies?.name ? (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{user.agencies.name}</span>
                ) : null}
              </span>
            ) : null}
          </span>
        </span>
      ),
    },
    {
      key: 'agency_id',
      label: 'Agency',
      render: (_value, user) => (
        <span style={{ fontSize: 'var(--text-sm)', color: user.agencies?.name ? 'var(--text-muted)' : 'var(--text-faint)' }}>
          {user.agencies?.name || '—'}
        </span>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (_value, user) => (
        <Badge tone={ROLE_TONES[user.role] || 'neutral'}>{roleLabel(user.role)}</Badge>
      ),
    },
    {
      key: 'is_banned',
      label: 'Status',
      render: (_value, user) =>
        user.is_banned ? (
          <Badge tone="idle">Inactive</Badge>
        ) : (
          <Badge tone="up" dot>
            Active
          </Badge>
        ),
    },
    {
      key: 'last_sign_in_at',
      label: 'Last Login',
      mono: true,
      render: (value) => (
        <span style={{ color: value ? 'var(--text-muted)' : 'var(--text-faint)' }}>
          {value ? formatDate(value as string) : 'Never'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Joined',
      mono: true,
      render: (value) => <span style={{ color: 'var(--text-muted)' }}>{formatDate(value as string)}</span>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_value, user) => (
        <KebabMenu
          label="User actions"
          actions={[
            { label: 'Edit User', icon: faPen, onClick: () => onEditUser(user) },
            user.role !== 'admin' &&
              user.id !== currentUserId && {
                label: 'Make Admin',
                icon: faUserShield,
                onClick: () =>
                  setConfirmAction({
                    open: true,
                    userId: user.id,
                    type: 'makeAdmin',
                    email: user.email,
                    isProcessing: false,
                  }),
              },
            user.id !== currentUserId && {
              label: user.is_banned ? 'Unban User' : 'Ban User',
              icon: user.is_banned ? faUserCheck : faBan,
              danger: !user.is_banned,
              onClick: () =>
                setConfirmAction({
                  open: true,
                  userId: user.id,
                  type: user.is_banned ? 'unban' : 'ban',
                  email: user.email,
                  isProcessing: false,
                }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <div className="overflow-x-auto">
        <Table<User>
          columns={phone ? columns.filter((c) => !PHONE_HIDDEN.has(c.key)) : columns}
          rows={filteredUsers}
          style={{ background: 'transparent', border: 'none', boxShadow: 'none', borderRadius: 0 }}
        />
      </div>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmAction.open}
        onClose={() => setConfirmAction((prev) => ({ ...prev, open: false }))}
        className="relative z-50"
      >
        <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
          <Dialog.Panel
            className="mx-auto w-full ck-menu-pop"
            style={{
              maxWidth: '400px',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-pop)',
              padding: 'var(--space-6)',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
              Team
            </div>
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--text-body)',
              }}
            >
              {confirmAction.type === 'ban'
                ? 'Ban User'
                : confirmAction.type === 'unban'
                  ? 'Unban User'
                  : 'Make Admin'}
            </Dialog.Title>

            <p
              style={{
                margin: 'var(--space-3) 0 var(--space-5)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
              }}
            >
              {confirmAction.type === 'ban'
                ? `Are you sure you want to ban ${confirmAction.email}? This will prevent them from signing in.`
                : confirmAction.type === 'unban'
                  ? `Are you sure you want to unban ${confirmAction.email}? This will allow them to sign in again.`
                  : `Are you sure you want to make ${confirmAction.email} an administrator? This will give them full access to manage the system.`}
            </p>

            <div className="flex justify-end" style={{ gap: '10px' }}>
              <Button
                variant="ghost"
                disabled={confirmAction.isProcessing}
                onClick={() => setConfirmAction((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </Button>
              <Button
                variant={confirmAction.type === 'ban' ? 'danger' : 'primary'}
                disabled={confirmAction.isProcessing}
                onClick={handleConfirmAction}
              >
                {confirmAction.isProcessing
                  ? 'Processing...'
                  : confirmAction.type === 'ban'
                    ? 'Ban User'
                    : confirmAction.type === 'unban'
                      ? 'Unban User'
                      : 'Make Admin'}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
